// API クライアント
// - セッショントークンを localStorage にキャッシュ
// - 401 で自動再取得 + リトライ
// - 失敗時はエラーをスローし、呼び出し側でフォールバックを判断

import type {
  SessionResponse,
  StationsResponse,
  StationsWithBlobsResponse,
  TideResponse,
} from '@tideprecision/shared';
import { getSetting, listStations, putStation, setSetting } from './state/idb.js';
import { computeTideOffline } from './lib/offlineCompute.js';
import { haversineKm } from './lib/distance.js';

const TOKEN_KEY = 'tide.session.token';
const TOKEN_EXP_KEY = 'tide.session.expires_at';

function getCachedToken(): string | null {
  const exp = localStorage.getItem(TOKEN_EXP_KEY);
  if (!exp) return null;
  if (new Date(exp).getTime() <= Date.now() + 30_000) return null; // 30 秒バッファ
  return localStorage.getItem(TOKEN_KEY);
}

function saveToken(t: string, expiresAt: string): void {
  localStorage.setItem(TOKEN_KEY, t);
  localStorage.setItem(TOKEN_EXP_KEY, expiresAt);
}

async function fetchSessionToken(): Promise<string> {
  const res = await fetch('/api/session', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  });
  if (!res.ok) throw new Error(`session 取得失敗: ${res.status}`);
  const body = (await res.json()) as SessionResponse;
  saveToken(body.token, body.expires_at);
  return body.token;
}

async function getToken(): Promise<string> {
  const cached = getCachedToken();
  if (cached) return cached;
  return fetchSessionToken();
}

async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  let token = await getToken();
  let res = await fetch(url, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  });
  if (res.status === 401) {
    // トークン無効化されている可能性 → 再取得して 1 度だけリトライ
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXP_KEY);
    token = await fetchSessionToken();
    res = await fetch(url, {
      ...init,
      headers: {
        ...(init.headers ?? {}),
        Authorization: `Bearer ${token}`,
      },
    });
  }
  return res;
}

/** ネットワーク成功時に IDB へ調和定数をキャッシュする */
function decodeBase64ToArrayBuffer(b64: string): ArrayBuffer {
  const bin = atob(b64);
  const buf = new ArrayBuffer(bin.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
  return buf;
}

async function cacheStationFromResponse(r: TideResponse): Promise<void> {
  try {
    const blob = decodeBase64ToArrayBuffer(r.harmonic_blob_base64);
    await putStation(
      {
        id: r.station.id,
        name: r.station.name,
        name_kana: r.station.name_kana,
        prefecture: r.station.prefecture,
        latitude: r.station.latitude,
        longitude: r.station.longitude,
      },
      blob,
    );
  } catch {
    // IDB 失敗はサイレント（オフライン計算に支障があれば後で気付く）
  }
}

export async function apiGetTide(
  lat: number,
  lon: number,
  date: string,
): Promise<TideResponse> {
  const url = `/api/tide?lat=${lat}&lon=${lon}&date=${encodeURIComponent(date)}`;
  try {
    const res = await authedFetch(url);
    if (!res.ok) throw new Error(`/api/tide ${res.status}`);
    const body = (await res.json()) as TideResponse;
    void cacheStationFromResponse(body);
    return body;
  } catch (e) {
    // オフライン or サーバ不調: 直近に取得した地点があればそれで計算
    const fallback = await tryOfflineNearest(lat, lon, date);
    if (fallback) return fallback;
    throw e;
  }
}

export async function apiGetTideByStation(
  stationId: number,
  date: string,
): Promise<TideResponse> {
  const url = `/api/tide/by-station?station_id=${stationId}&date=${encodeURIComponent(date)}`;
  try {
    const res = await authedFetch(url);
    if (!res.ok) throw new Error(`/api/tide/by-station ${res.status}`);
    const body = (await res.json()) as TideResponse;
    void cacheStationFromResponse(body);
    return body;
  } catch (e) {
    const fallback = await computeTideOffline(stationId, date);
    if (fallback) return fallback;
    throw e;
  }
}

/** オフライン時、IDB に保存済み地点から最近傍を計算で求める */
async function tryOfflineNearest(lat: number, lon: number, date: string): Promise<TideResponse | null> {
  const all = await listStations();
  if (all.length === 0) return null;
  let best = all[0]!;
  let bestDist = Infinity;
  for (const s of all) {
    const d = haversineKm(lat, lon, s.meta.latitude, s.meta.longitude);
    if (d < bestDist) {
      best = s;
      bestDist = d;
    }
  }
  const r = await computeTideOffline(best.meta.id, date);
  if (!r) return null;
  return { ...r, station: { ...r.station, distance_km: bestDist } };
}

export async function apiGetStations(): Promise<StationsResponse> {
  const res = await authedFetch('/api/stations');
  if (!res.ok) throw new Error(`/api/stations ${res.status}`);
  return (await res.json()) as StationsResponse;
}

export async function apiGetStationsWithBlobs(): Promise<StationsWithBlobsResponse> {
  const res = await authedFetch('/api/stations/with-blobs');
  if (!res.ok) throw new Error(`/api/stations/with-blobs ${res.status}`);
  return (await res.json()) as StationsWithBlobsResponse;
}

/**
 * 全地点の調和定数を IndexedDB にバックグラウンド プリフェッチ。
 * 完全オフライン化のため起動後に呼ぶ。24 時間に 1 回までしか走らない（IDB の settings で頻度制御）。
 * オンライン時のみ動作、失敗してもサイレント（次回起動時に再試行）。
 */
const PREFETCH_KEY = 'lastPrefetchAt';
const PREFETCH_INTERVAL_MS = 24 * 60 * 60 * 1000;

export async function prefetchAllStations(): Promise<{ prefetched: number; skipped: boolean }> {
  if (!navigator.onLine) return { prefetched: 0, skipped: true };
  const last = await getSetting<number>(PREFETCH_KEY);
  if (last && Date.now() - last < PREFETCH_INTERVAL_MS) {
    return { prefetched: 0, skipped: true };
  }
  try {
    const r = await apiGetStationsWithBlobs();
    for (const s of r.stations) {
      const blob = decodeBase64ToArrayBuffer(s.harmonic_blob_base64);
      await putStation(
        {
          id: s.id,
          name: s.name,
          name_kana: s.name_kana,
          prefecture: s.prefecture,
          latitude: s.latitude,
          longitude: s.longitude,
        },
        blob,
      );
    }
    await setSetting(PREFETCH_KEY, Date.now());
    return { prefetched: r.stations.length, skipped: false };
  } catch {
    // オフライン等で失敗したら無視（次回再試行）
    return { prefetched: 0, skipped: true };
  }
}
